/**
 * Every usage event the client UI sends, to Datadog Product Analytics as a RUM action and to
 * Microsoft Clarity as a custom event. See docs/MARKET_CONTEXT_USAGE_ANALYTICS.md.
 *
 * Each entry is `{ name, context, clarityTags? }`:
 * - `name` is the action's name in Datadog and the event's in Clarity, `market_<surface>_<what>` in
 *   snake_case. Actions belong to the RingbaX RUM application, so no `rx-` prefix is needed.
 * - `context` lists the keys the event may carry. The plugin drops any other key, so a view cannot
 *   send a campaign name by accident. Context holds enums, counts, ids and dollar amounts, never a
 *   name, and nothing the buyer's own screen does not show (no evidence or confidence levels).
 *   Amounts are numbers in USD, named `…InUSD`; minimums are whole seconds, named `…Seconds`.
 * - `clarityTags`, where present, maps the event's context to the Clarity tags it sets, enums only.
 *   Clarity gets the event's name and these tags, so no amount reaches it.
 *
 * Send one with `this.$track(USAGE_EVENTS.x.name, context)`, or `this.$trackOnce(...)` for a
 * `*_shown` event (see `src/plugins/usageAnalytics.js`). Both refuse a name that is not listed here.
 *
 * @type {Readonly<Record<string, { name: string, context: string[], clarityTags?: (context: object) => Record<string, string> }>>}
 */
export const USAGE_EVENTS = Object.freeze({
  // The budget step (Budget.vue on the create, edit, softphone create and softphone edit pages)

  // The card renders with data. Sent by the card, once per state
  marketCardShown: {
    name: 'market_card_shown',
    context: ['page', 'routing', 'state', 'setupFrom', 'losesOnConversion', 'ownBidInUSD', 'suggestedBidInUSD', 'suggestedMinimumSeconds', 'categoryId'],
    clarityTags: ({ state }) => ({ market_card_state: state, market_intelligence: 'on' })
  },

  // The panel, the card's fallback while rx-market-intelligence is off, renders with data. Sent by
  // the panel, once per category
  marketPanelShown: {
    name: 'market_panel_shown',
    context: ['page', 'layout', 'ownBidInUSD', 'suggestedBidInUSD', 'categoryId'],
    clarityTags: () => ({ market_intelligence: 'off' })
  },

  // The card's "Use these" or "Use $X", or a tile's "Use $X". Static only
  marketCardUseClicked: {
    name: 'market_card_use_clicked',
    context: ['page', 'routing', 'target', 'setsMinimum', 'previousBidInUSD', 'bidInUSD', 'minimumSeconds']
  },

  // A panel tier's "Use $X"
  marketPanelUseClicked: {
    name: 'market_panel_use_clicked',
    context: ['page', 'tier', 'previousBidInUSD', 'bidInUSD']
  },

  // The RingbaRTB or Programmatic guidance copied
  marketGuidanceCopied: {
    name: 'market_guidance_copied',
    context: ['page', 'routing']
  },

  // A campaign saved from one of the four pages, when the card or the panel rendered with data
  // during the visit. Sent by the page
  marketBudgetSaved: {
    name: 'market_budget_saved',
    context: [
      'page', 'campaignId', 'routing', 'surface', 'bidChanged', 'minimumChanged', 'source', 'used',
      'bidVsSuggested', 'minimumVsSuggested', 'bidInUSD', 'suggestedBidInUSD', 'minimumSeconds', 'suggestedMinimumSeconds'
    ]
  },

  // The campaigns list (ListBuyerCampaigns.vue)

  // The market columns have their positions. Once per load
  marketListShown: {
    name: 'market_list_shown',
    context: [
      'intelligence', 'rowsWithRaiseChip', 'rowsWithSwitchChip', 'rowsWithLegacyChip', 'rowsWithAdvice',
      'rowsWithConversionAdvice', 'rowsWithNoSuggestionReason', 'rowsWithNone'
    ],
    clarityTags: ({ intelligence }) => ({ market_intelligence: intelligence ? 'on' : 'off' })
  },

  // The missed-calls banner renders. Once per load
  marketBannerShown: {
    name: 'market_banner_shown',
    context: ['intelligence']
  },

  // The banner's "Review below-market bids"
  marketBannerReviewClicked: {
    name: 'market_banner_review_clicked',
    context: []
  },

  // A market tooltip opens. Once per kind per load
  marketTooltipShown: {
    name: 'market_tooltip_shown',
    context: ['tooltip']
  },

  // A chip opens the confirmation
  marketSuggestionOpened: {
    name: 'market_suggestion_opened',
    context: ['campaignId', 'chip', 'change', 'routing', 'currentBidInUSD', 'suggestedBidInUSD', 'currentMinimumSeconds', 'suggestedMinimumSeconds', 'missedCalls']
  },

  // The confirmation's confirm, applied or refused
  marketSuggestionConfirmed: {
    name: 'market_suggestion_confirmed',
    context: [
      'campaignId', 'chip', 'change', 'routing', 'currentBidInUSD', 'suggestedBidInUSD', 'currentMinimumSeconds',
      'suggestedMinimumSeconds', 'missedCalls', 'outcome', 'source'
    ]
  },

  // The confirmation closed without a confirm
  marketSuggestionDismissed: {
    name: 'market_suggestion_dismissed',
    context: [
      'campaignId', 'chip', 'change', 'routing', 'currentBidInUSD', 'suggestedBidInUSD', 'currentMinimumSeconds',
      'suggestedMinimumSeconds', 'missedCalls', 'trigger'
    ]
  },

  // The inline bid editor saved a row's bid
  marketInlineBidSaved: {
    name: 'market_inline_bid_saved',
    context: ['campaignId', 'source', 'rowSuggestion', 'previousBidInUSD', 'bidInUSD', 'suggestedBidInUSD']
  }
})

/**
 * Where a saved bid came from, carried by every event that saves one: the events' copy of the
 * backend's `bidChangeSources`, which only the list's chips send.
 */
export const BID_SOURCES = Object.freeze({
  // A list chip's confirmation, or a budget-step button's value saved unchanged
  suggestion: 'suggestion',
  // A budget-step button set it, then the buyer changed it before saving
  suggestionEdited: 'suggestion_edited',
  manual: 'manual'
})

// A campaign's routing type (campaignRoutingTypes) as the events name it
const ROUTING_NAMES = Object.freeze({
  Static: 'static',
  RingbaRTB: 'rtb',
  Programmatic: 'programmatic'
})

export function usageRoutingOf(routingType) {
  return Object.prototype.hasOwnProperty.call(ROUTING_NAMES, routingType) ? ROUTING_NAMES[routingType] : null
}

/**
 * The entry for an event name, or `undefined` when the name is not in `USAGE_EVENTS`.
 */
export function findUsageEvent(name) {
  return Object.values(USAGE_EVENTS).find(definition => definition.name === name)
}
